import { FC, useMemo } from 'react';
import { useTenantID } from '@/hooks/useTenantID';
import Button from '../core/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminTypeColor } from '@/types/administrator';
import { cn } from '@/helpers/classnames';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';
import { IconEdit, IconEye, IconSmartcard } from '@/icons';
import { CurrentUserBadge } from '@/components/CurrentUserBadge';

interface Props {
  administrator: ExtendedUserDTO;
  className?: string;
  currentUserEmail?: string;
}

export const DepartmentAdminsListItem: FC<Props> = ({
  administrator,
  className,
  currentUserEmail,
}) => {
  const tenantId = useTenantID();
  const { id, firstname, lastname, email, adminType } = administrator;
  const { formatMessage } = useIntl();

  const isCurrentUser = currentUserEmail && email === currentUserEmail;

  const level = useMemo(() => {
    switch (adminType) {
      case 'deptAdmin':
        return {
          color: adminTypeColor.deptAdmin,
          title: formatMessage({ defaultMessage: 'Skriv' }),
          icon: <IconEdit className="size-4" />,
        };
      case 'deptBearerManager':
        return {
          color: adminTypeColor.deptBearerManager,
          title: formatMessage({ defaultMessage: 'Bärarhantering' }),
          icon: <IconSmartcard className="size-4" />,
        };
      default:
        return {
          color: adminTypeColor.deptReader,
          title: formatMessage({ defaultMessage: 'Läs' }),
          icon: <IconEye className="size-4" />,
        };
    }
  }, [adminType, formatMessage]);

  return (
    <Button
      to="/$tenantId/administrators/$administratorId"
      params={{ tenantId, administratorId: id }}
      className={cn(
        'h-fit w-full min-w-0 flex-col items-start gap-1 overflow-hidden font-normal',
        className,
      )}
    >
      <div className="flex w-full min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate pr-1 font-medium">
            {firstname ?? ''} {lastname ?? ''}
            {!firstname && !lastname && (
              <i>
                <FormattedMessage defaultMessage="Anonym administratör" />
              </i>
            )}
          </span>
          {isCurrentUser && <CurrentUserBadge />}
        </div>
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full px-2 py-1',
            level.color,
          )}
          title={level.title}
        >
          {level.icon}
        </span>
      </div>
      <p className="w-full min-w-0 truncate text-left text-text-700">{email}</p>
    </Button>
  );
};
